import { getRoomBoardCondition } from './boardConditions';
import { CORE_SECTOR } from './runConstants';

const relic = (
  id,
  name,
  description,
  rarity,
  animation,
  rollCondition = null,
  rollBonus = 0,
  extra = {}
) => ({
  id,
  name,
  description,
  rarity,
  category: extra.category || (extra.activeAbility ? 'Special' : rarity),
  icon: id,
  animation,
  rollCondition,
  rollBonus,
  ...extra,
});

// The first thirteen relics are the original run relics. Keeping their ids
// stable means saved runs and existing player progress remain valid.
const ORIGINAL_RELICS = [
  relic(
    'luckySeven',
    'Lucky Seven',
    'If your roll contains a 7 after tile effects, add 20 power.',
    'Common',
    'lucky-seven',
    'contains7',
    20,
    { shopPrice: 4 }
  ),
  relic(
    'mirrorChip',
    'Mirror Chip',
    'Once per area, flip the digits on your die if it helps: 29 becomes 92. This happens automatically before tile and relic effects.',
    'Common',
    'mirror-chip',
    null,
    0,
    { category: 'Special', shopPrice: 10 }
  ),
  relic(
    'firewall',
    'Firewall',
    'Block 1 damage from every enemy counterattack.',
    'Epic',
    'firewall',
    null,
    0,
    { damageReduction: 1, shopPrice: 10 }
  ),
  relic(
    'patchKit',
    'Patch Kit',
    'Roll 72 or higher on the die to heal 1 HP before the enemy hits back.',
    'Common',
    'patch-kit',
    null,
    0,
    { healAmount: 1, healCondition: 'natural72', shopPrice: 5 }
  ),
  relic(
    'glitchBattery',
    'Glitch Battery',
    'If your roll is 10 or lower after tile effects, add 30 power.',
    'Common',
    'glitch-battery',
    'low',
    30,
    { shopPrice: 2 }
  ),
  relic(
    'scavenger',
    'Scavenger',
    'Activating a shard tile adds +12 power.',
    'Uncommon',
    'scavenger',
    'shard-tile',
    12,
    { shopPrice: 5 }
  ),
  relic(
    'overclock',
    'Overclock',
    'Every roll gains +12, but enemy attacks deal 1 extra damage.',
    'Uncommon',
    'overclock',
    'always',
    12,
    { shopPrice: 3 }
  ),
  relic(
    'echoCore',
    'Echo Core',
    'Matching digits such as 22 or 77 gain +18.',
    'Uncommon',
    'echo-core',
    'double',
    18,
    { shopPrice: 2 }
  ),
  relic(
    'vaultKey',
    'Vault Key',
    'Shield tiles add +12 power.',
    'Uncommon',
    'vault-key',
    'shield-tile',
    12,
    { shopPrice: 6 }
  ),
  relic(
    'prismDrive',
    'Prism Drive',
    'If your roll is even after tile effects, add 8 power.',
    'Uncommon',
    'prism-drive',
    'even',
    8,
    { shopPrice: 4 }
  ),
  relic(
    'lastStand',
    'Last Stand',
    'Starting a throw at 4 HP or less blocks 2 counterpower.',
    'Rare',
    'last-stand',
    null,
    0,
    { shopPrice: 8, damageReduction: 2, blockCondition: 'low-hp' }
  ),
  relic(
    'bountyChip',
    'Bounty Chip',
    'Defeating an enemy with at least 25 excess power grants 2 extra shards.',
    'Rare',
    'bounty-chip',
    null,
    0,
    { shopPrice: 8, successShards: 2, shardCondition: 'overkill' }
  ),
  relic(
    'coreBreaker',
    'Core Breaker',
    'Power Up throws gain +32 power.',
    'Epic',
    'core-breaker',
    'powered',
    32,
    { shopPrice: 12 }
  ),
];

// New relics are deliberately data-driven. The icon and animation key are
// part of each relic so adding future relics does not require another switch.
const NEW_RELICS = [
  // Common relics
  [
    'signalLantern',
    'Signal Lantern',
    'Rolls of 75+ gain +6.',
    'Common',
    'signal-lantern',
    'high',
    6,
  ],
  [
    'staticLure',
    'Static Lure',
    'Odd rolls gain +5.',
    'Common',
    'static-lure',
    'odd',
    5,
  ],
  [
    'byteBloom',
    'Byte Bloom',
    'Activating a positive numeric tile adds +4 power.',
    'Common',
    'byte-bloom',
    'positive-tile',
    4,
    {},
  ],
  [
    'coinFlip',
    'Coin Flip',
    'Even rolls gain +5.',
    'Common',
    'coin-flip',
    'even',
    5,
  ],
  [
    'rustedDice',
    'Rusted Dice',
    'Every roll gains +2.',
    'Common',
    'rusted-dice',
    'always',
    2,
  ],
  [
    'pulseNeedle',
    'Pulse Needle',
    'Multiples of 5 gain +8.',
    'Common',
    'pulse-needle',
    'multiple5',
    8,
  ],
  [
    'holoLens',
    'Holo Lens',
    'Settling in the center row adds +12 power.',
    'Common',
    'holo-lens',
    'center-row',
    12,
    {},
  ],
  [
    'circuitMoss',
    'Living Circuit',
    'Healing tiles add +8 power.',
    'Common',
    'circuit-moss',
    'heal-tile',
    8,
  ],
  [
    'scrapMagnet',
    'Scrap Magnet',
    'Defeating an enemy grants 1 extra shard.',
    'Common',
    'scrap-magnet',
    null,
    0,
    { successShards: 1 },
  ],
  [
    'nanoBandage',
    'Nano Bandage',
    'Activating at least one healing tile grants an extra + 1 Max HP.',
    'Common',
    'nano-bandage',
    null,
    0,
    { healAmount: 1, healCondition: 'heal-tile' },
  ],
  [
    'safeMode',
    'Safe Mode',
    'Launching at 50% power or less adds +10 power and blocks 1 counterpower.',
    'Common',
    'safe-mode',
    'soft-launch',
    4,
    { damageReduction: 1, blockCondition: 'soft-launch', shopPrice: 4 },
  ],
  [
    'warmCache',
    'Warm Reactor',
    'At half health or less, add 25 damage if your roll is 35 or lower after tile effects.',
    'Common',
    'warm-cache',
    'low-roll-low-hp',
    25,
    {},
  ],
  [
    'tinyOracle',
    'Tiny Oracle',
    'If your roll is 12, 23, 34, 45, 56, 67, 78, or 89 after tile effects, add 25 damage.',
    'Common',
    'tiny-oracle',
    'ascending-digits',
    25,
  ],
  [
    'sparkPlug',
    'Spark Plug',
    'Rolls of 20 or lower gain +10.',
    'Common',
    'spark-plug',
    'low20',
    10,
  ],
  [
    'driftKey',
    'Slipstream Key',
    'After a portal transit, gain +25 power.',
    'Common',
    'drift-key',
    'portal',
    25,
  ],
  [
    'coldBoot',
    'Cold Boot',
    'If your roll is 35 or lower after tile effects, add 15 power.',
    'Common',
    'cold-boot',
    'low35',
    15,
  ],
  [
    'calibrationBead',
    'Calibration Bead',
    'If your roll ends in 1 after tile effects, add 20 power.',
    'Common',
    'calibration-bead',
    'ends-one',
    20,
  ],
  [
    'triadSpark',
    'Triad Spark',
    'Multiples of 3 gain +6 power.',
    'Common',
    'triad-spark',
    'multiple3',
    6,
  ],
  [
    'hollowBuffer',
    'Hollow Buffer',
    'At 4 HP or less, rolls gain +25 power.',
    'Common',
    'hollow-buffer',
    'low-hp',
    25,
  ],

  // Uncommon relics
  [
    'phaseShifter',
    'Phase Shifter',
    'Rolls of 40+ gain +9.',
    'Uncommon',
    'phase-shifter',
    'high40',
    9,
  ],
  [
    'mirrorDust',
    'Mirror Dust',
    'A roll reversed by Mirror Chip gains +16 power.',
    'Uncommon',
    'mirror-dust',
    'mirrored',
    16,
    {},
  ],
  [
    'overchargeCoil',
    'Overcharge Coil',
    'Power Up throws gain +14 power.',
    'Uncommon',
    'overcharge-coil',
    'powered',
    14,
    {},
  ],
  [
    'medicDrone',
    'Medic Drone',
    'Defeating an enemy grants + 1 Max HP.',
    'Uncommon',
    'medic-drone',
    null,
    0,
    { successHeal: 1, shopPrice: 5 },
  ],
  [
    'shardSiphon',
    'Kinetic Siphon',
    'Each board bump adds +4 power, up to +12.',
    'Uncommon',
    'shard-siphon',
    'bumped',
    4,
    { bonusPerBump: true },
  ],
  [
    'ironRoot',
    'Iron Root',
    'After your first attack against an enemy, block 2 damage from each counterattack.',
    'Uncommon',
    'iron-root',
    null,
    0,
    { damageReduction: 2, blockCondition: 'follow-up' },
  ],
  [
    'targetingGrid',
    'Momentum Grid',
    'Launching at 85% power or higher adds +12 power.',
    'Uncommon',
    'targeting-grid',
    'full-power',
    12,
  ],
  [
    'phaseGlass',
    'Phase Glass',
    'Activating Phase Anchor adds +14 power to that throw.',
    'Uncommon',
    'phase-glass',
    'anchored',
    14,
  ],
  [
    'deadPixel',
    'Dead Pixel',
    'Rolls of 25 or lower gain +14.',
    'Uncommon',
    'dead-pixel',
    'low25',
    14,
  ],
  [
    'chargeRibbon',
    'Charge Ribbon',
    'Multiples of 10 gain +12.',
    'Uncommon',
    'charge-ribbon',
    'multiple10',
    12,
  ],
  [
    'ghostProtocol',
    'Ghost Protocol',
    'A throw with no board bumps gains +7 power.',
    'Uncommon',
    'ghost-protocol',
    'no-bumps',
    7,
  ],
  [
    'redlineValve',
    'Redline Valve',
    'Rolls of 85+ gain +12.',
    'Uncommon',
    'redline-valve',
    'very-high',
    12,
    { minRoll: 85 },
  ],
  [
    'echoThread',
    'Echo Thread',
    'Rolls with two different digits gain +5.',
    'Uncommon',
    'echo-thread',
    'different',
    5,
  ],
  [
    'recoveryLoop',
    'Recovery Loop',
    'Activating Phase Anchor grants + 2 Max HP before the enemy hits back.',
    'Uncommon',
    'recovery-loop',
    null,
    0,
    { healAmount: 2, healCondition: 'anchored', shopPrice: 5 },
  ],
  [
    'shardCompass',
    'Shard Compass',
    'Defeating an enemy on your first attack grants 2 extra shards.',
    'Uncommon',
    'shard-compass',
    null,
    0,
    { successShards: 2, shardCondition: 'first-attack' },
  ],
  [
    'targetingPrism',
    'Vector Prism',
    'Using at least two accepted board bumps adds +16 power.',
    'Uncommon',
    'targeting-prism',
    'two-bumps',
    16,
    {},
  ],
  [
    'overdrawCoil',
    'Overdraw Coil',
    'If your roll is 50 or lower after tile effects, add 16 power.',
    'Uncommon',
    'overdraw-coil',
    'low50',
    16,
  ],
  [
    'treasureMender',
    'Pulse Stitch',
    'Healing tiles add +12 power.',
    'Uncommon',
    'treasure-mender',
    'heal-tile',
    12,
    {},
  ],
  [
    'overflowCache',
    'Overflow Cache',
    'Defeating an enemy with a Power Up throw grants 3 extra shards.',
    'Uncommon',
    'overflow-cache',
    null,
    0,
    { successShards: 3, shardCondition: 'powered' },
  ],

  // Rare relics
  [
    'quantumDie',
    'Quantum Die',
    'Every roll gains +15.',
    'Rare',
    'quantum-die',
    'always',
    15,
  ],
  [
    'redCipher',
    'Red Cipher',
    'Rolls containing 7 gain +30.',
    'Rare',
    'red-cipher',
    'contains7',
    30,
  ],
  [
    'voidCompass',
    'Void Compass',
    'If your roll is 70 or higher after tile effects, add 18 power.',
    'Rare',
    'void-compass',
    'high',
    18,
    { minRoll: 70 },
  ],
  [
    'crownCache',
    'Royal Recoil',
    'Follow-up attacks against the same enemy gain +18 power.',
    'Rare',
    'crown-cache',
    'follow-up',
    18,
    {},
  ],
  [
    'repairSwarm',
    'Repair Swarm',
    'Defeating an enemy grants + 1 Max HP.',
    'Rare',
    'repair-swarm',
    null,
    0,
    { successHeal: 1 },
  ],
  [
    'nullifier',
    'Nullifier',
    'Block 2 damage from every enemy counterattack.',
    'Rare',
    'nullifier',
    null,
    0,
    { damageReduction: 2 },
  ],
  [
    'perfectLens',
    'Perfect Lens',
    'If your roll has matching digits after tile effects, such as 22 or 77, add 24 power.',
    'Rare',
    'perfect-lens',
    'double',
    24,
  ],
  [
    'overdriveHeart',
    'Overdrive Heart',
    'Rolls of 90+ gain +20.',
    'Rare',
    'overdrive-heart',
    'very-high',
    20,
    { minRoll: 90 },
  ],
  [
    'treasureMap',
    'Constellation Map',
    'Activating a negative board tile grants +22 power.',
    'Rare',
    'treasure-map',
    'negative-tile',
    22,
  ],
  [
    'doubleHelix',
    'Double Helix',
    'If your roll is 21, 32, 43, 54, 65, 76, 87, or 98 after tile effects, add 28 power.',
    'Rare',
    'double-helix',
    'descending-digits',
    28,
    {},
  ],
  [
    'neonAnvil',
    'Neon Anvil',
    'Even rolls gain +15.',
    'Rare',
    'neon-anvil',
    'even',
    15,
  ],
  [
    'luckyOrbit',
    'Lucky Orbit',
    'Odd rolls gain +15.',
    'Rare',
    'lucky-orbit',
    'odd',
    15,
  ],
  [
    'bossScanner',
    'Fracture Scanner',
    'Attacks against enemies at half HP or less gain +20 power.',
    'Rare',
    'boss-scanner',
    'wounded-enemy',
    20,
  ],
  [
    'deepReserve',
    'Deep Reserve',
    'Defeating an enemy grants 2 extra shards.',
    'Rare',
    'deep-reserve',
    null,
    0,
    { successShards: 2 },
  ],
  [
    'phoenixThread',
    'Phoenix Thread',
    'Starting a throw at 4 HP or less grants + 1 Max HP before the enemy hits back.',
    'Rare',
    'phoenix-thread',
    null,
    0,
    { healAmount: 1, healCondition: 'low-hp' },
  ],
  [
    'shardBloom',
    'Shard Bloom',
    'Defeating an enemy after activating at least 3 board tiles grants 3 extra shards.',
    'Rare',
    'shard-bloom',
    null,
    0,
    { successShards: 3, shardCondition: 'three-tiles' },
  ],
  [
    'gravityWell',
    'Gravity Well',
    'Each activated negative tile adds +12 power, up to +36.',
    'Rare',
    'gravity-well',
    'negative-tile',
    12,
    { bonusPerNegativeTile: true, shopPrice: 9 },
  ],
  [
    'prismaticSeal',
    'Prismatic Seal',
    'Every roll gains +10 and enemy attacks deal 1 less damage.',
    'Rare',
    'prismatic-seal',
    'always',
    10,
    { damageReduction: 1 },
  ],

  // Epic relics
  [
    'starForge',
    'Star Forge',
    'Every roll gains +22.',
    'Epic',
    'star-forge',
    'always',
    22,
  ],
  [
    'singularityKey',
    'Singularity Key',
    'Matching digits gain +35.',
    'Epic',
    'singularity-key',
    'double',
    35,
  ],
  [
    'realityShredder',
    'Reality Shredder',
    'Critical Hit tiles add +28 power.',
    'Epic',
    'reality-shredder',
    'critical-tile',
    28,
  ],
  [
    'immortalCircuit',
    'Immortal Circuit',
    'Block 1 damage from every enemy counterattack.',
    'Epic',
    'immortal-circuit',
    null,
    0,
    { damageReduction: 1 },
  ],
  [
    'treasureEngine',
    'Treasure Engine',
    'Defeating an enemy grants 3 extra shards.',
    'Epic',
    'treasure-engine',
    null,
    0,
    { successShards: 3 },
  ],
  [
    'lifeweaver',
    'Lifeweaver',
    'Defeating an enemy grants + 2 Max HP.',
    'Epic',
    'lifeweaver',
    null,
    0,
    { successHeal: 2 },
  ],
  [
    'omegaLens',
    'Omega Lens',
    'Throws that activate at least 3 board tiles gain +30 power.',
    'Epic',
    'omega-lens',
    'three-tiles',
    30,
  ],
  [
    'crownOfThorns',
    'Crown of Thorns',
    'Rolls of 80+ gain +28.',
    'Epic',
    'crown-of-thorns',
    'very-high',
    28,
  ],
  [
    'phantomVault',
    'Phantom Vault',
    'After a portal transit, gain +30 power.',
    'Epic',
    'phantom-vault',
    'portal',
    30,
  ],
  [
    'chronoShard',
    'Chrono Shard',
    'Multiples of 10 gain +30.',
    'Epic',
    'chrono-shard',
    'multiple10',
    30,
  ],
  [
    'apexBattery',
    'Apex Battery',
    'Rolls of 10 or lower gain +45.',
    'Epic',
    'apex-battery',
    'low',
    45,
  ],
  [
    'stormCrown',
    'Storm Crown',
    'Odd rolls gain +25.',
    'Epic',
    'storm-crown',
    'odd',
    25,
  ],
  [
    'mirrorCathedral',
    'Mirror Cathedral',
    'Even rolls gain +25.',
    'Epic',
    'mirror-cathedral',
    'even',
    25,
  ],
  [
    'redshiftCore',
    'Redshift Core',
    'If your roll is 90 or higher after tile effects, add 40 power.',
    'Epic',
    'redshift-core',
    'very-high',
    40,
    { minRoll: 90 },
  ],
  [
    'guardianHalo',
    'Guardian Halo',
    'Your first attack against each enemy gains +20 power and blocks 3 counterpower.',
    'Epic',
    'guardian-halo',
    'first-attack',
    20,
    { damageReduction: 3, blockCondition: 'first-attack', shopPrice: 14 },
  ],
  [
    'mythicScavenger',
    'Mythic Scavenger',
    'Defeating an enemy grants 2 extra shards and + 2 Max HP.',
    'Epic',
    'mythic-scavenger',
    null,
    0,
    { successShards: 2, successHeal: 2 },
  ],
  [
    'corePrism',
    'Core Prism',
    'Every attack gains +14 power; an even roll after tile effects gains another +10.',
    'Epic',
    'core-prism',
    'always',
    14,
    { evenBonus: 10 },
  ],
  [
    'eventHorizon',
    'Event Horizon',
    'Every roll gains +18; enemy attacks deal 1 less damage.',
    'Epic',
    'event-horizon',
    'always',
    18,
    { damageReduction: 1 },
  ],
  [
    'goldenThread',
    'Golden Thread',
    'Defeating an enemy without any board bumps grants 4 extra shards.',
    'Epic',
    'golden-thread',
    null,
    0,
    { successShards: 4, shardCondition: 'no-bumps' },
  ],

  // 20 Legendary relics.
  [
    'worldCore',
    'World Core',
    'Every roll gains +35.',
    'Legendary',
    'world-core',
    'always',
    35,
    { shopPrice: 18 },
  ],
  [
    'infinityDice',
    'Infinity Dice',
    'Every roll gains +30; defeating an enemy grants + 3 Max HP.',
    'Legendary',
    'infinity-dice',
    'always',
    30,
    { successHeal: 3, shopPrice: 20 },
  ],
  [
    'realityCrown',
    'Reality Crown',
    'The first attack against each enemy gains +50 power.',
    'Legendary',
    'reality-crown',
    'first-attack',
    50,
  ],
  [
    'architectKey',
    'Architect Key',
    'Every attack gains +20 power, plus +20 after a portal transit.',
    'Legendary',
    'architect-key',
    'always',
    20,
    { portalBonus: 20 },
  ],
  [
    'phoenixEngine',
    'Phoenix Engine',
    'Once per run, starting a throw at 5 HP or less heals 5 HP before the enemy hits back. Any overflow increases Max HP.',
    'Legendary',
    'phoenix-engine',
    null,
    0,
    { healAmount: 5, healCondition: 'five-hp', healOncePerRun: true },
  ],
  [
    'voidHeart',
    'Void Heart',
    'Block 2 damage from every enemy counterattack.',
    'Legendary',
    'void-heart',
    null,
    0,
    { damageReduction: 2 },
  ],
  [
    'cosmicVault',
    'Cosmic Vault',
    'Defeating an enemy grants 4 extra shards.',
    'Legendary',
    'cosmic-vault',
    null,
    0,
    { successShards: 4 },
  ],
  [
    'absoluteLens',
    'Absolute Lens',
    'Link at least two different throw actions (bump, Anchor, portal, or Chain) to gain +40 power.',
    'Legendary',
    'absolute-lens',
    'linked-throw',
    40,
  ],
  [
    'quantumCrown',
    'Quantum Crown',
    'Matching digits gain +50.',
    'Legendary',
    'quantum-crown',
    'double',
    50,
  ],
  [
    'omegaBattery',
    'Omega Battery',
    'Rolls of 10 or lower gain +65.',
    'Legendary',
    'omega-battery',
    'low',
    65,
  ],
  [
    'blackStar',
    'Black Star',
    'If your roll is zero or lower after tile effects, add 70 power.',
    'Legendary',
    'black-star',
    'nonpositive',
    70,
    {},
  ],
  [
    'genesisShard',
    'Genesis Shard',
    'Once per run, kill an enemy with more than 50 damage of overkill to heal 10 HP and gain 10 shards. Any healing overflow increases Max HP.',
    'Legendary',
    'genesis-shard',
    null,
    0,
    {
      successHeal: 10,
      successShards: 10,
      successCondition: 'overkill-50',
      healOncePerRun: true,
    },
  ],
  [
    'circuitSovereign',
    'Circuit Sovereign',
    'Every attack gains +20 power; Power Up adds another +24.',
    'Legendary',
    'circuit-sovereign',
    'always',
    20,
    { poweredBonus: 24 },
  ],
  [
    'prismaticEternity',
    'Prismatic Eternity',
    'Every roll gains +20; even rolls gain another +10.',
    'Legendary',
    'prismatic-eternity',
    'always',
    20,
    { evenBonus: 10 },
  ],
  [
    'lastEquation',
    'Last Equation',
    'Rolls of 75+ gain +25.',
    'Legendary',
    'last-equation',
    'high',
    25,
  ],
  [
    'starEater',
    'Star Eater',
    'Attacks against enemies at half HP or less gain +40 power.',
    'Legendary',
    'star-eater',
    'wounded-enemy',
    40,
    {},
  ],
  [
    'chronarchSeal',
    'Chronarch Seal',
    'Multiples of 10 gain +50.',
    'Legendary',
    'chronarch-seal',
    'multiple10',
    50,
  ],
  [
    'glitchGenesis',
    'Glitch Genesis',
    'Every roll gains +20 and every enemy counterattack deals 2 less damage.',
    'Legendary',
    'glitch-genesis',
    'always',
    20,
    { damageReduction: 2, shopPrice: 22 },
  ],
  [
    'theLastLight',
    'The Last Light',
    'At 4 HP or less, every roll gains +50.',
    'Legendary',
    'the-last-light',
    'low-hp',
    50,
  ],
];

const BOARD_RELICS = [
  relic(
    'weatherVane',
    'Weather Vane',
    'Crosswind and Magnetic Field boards add +6 power.',
    'Common',
    'weather-vane',
    'force-field',
    6,
    { boardConditionTypes: ['crosswind', 'magnetic'] }
  ),
  relic(
    'cryoTraction',
    'Cryo Traction',
    'Settling on an Ice Lane adds +18 power.',
    'Uncommon',
    'cryo-traction',
    'ice-landing',
    18,
    { boardConditionTypes: ['ice'] }
  ),
  relic(
    'riftConductor',
    'Rift Conductor',
    'A portal transit and an accepted board bump in the same throw add +24 power.',
    'Rare',
    'rift-conductor',
    'portal-bump',
    24
  ),
  relic(
    'orbitEngine',
    'Orbit Engine',
    'Using at least two board bumps on a Low Gravity board adds +36 power.',
    'Epic',
    'orbit-engine',
    'low-gravity-bumps',
    36,
    { boardConditionTypes: ['lowGravity'] }
  ),
];

const TACTICAL_RELICS = [
  relic(
    'cornerBeacon',
    'Corner Beacon',
    'Settling on one of the four corner tiles adds +14 power.',
    'Common',
    'corner-beacon',
    'corner-tile',
    14
  ),
  relic(
    'balancedGyro',
    'Balanced Gyro',
    'Launching above 50% and below 75% power adds +10 power.',
    'Uncommon',
    'balanced-gyro',
    'measured-launch',
    10
  ),
  relic(
    'relayCapacitor',
    'Relay Capacitor',
    'Routing a Relay to another tile adds +22 power, once per throw.',
    'Rare',
    'relay-capacitor',
    'relay-routed',
    22
  ),
  relic(
    'anchorPlating',
    'Anchor Plating',
    'Activating Phase Anchor and using a board bump on the same throw blocks 3 counterpower.',
    'Rare',
    'anchor-plating',
    null,
    0,
    { damageReduction: 3, blockCondition: 'anchor-bump' }
  ),
  relic(
    'hazardDividend',
    'Hazard Dividend',
    'Defeating an enemy after activating a negative board tile grants 4 extra shards.',
    'Epic',
    'hazard-dividend',
    null,
    0,
    { successShards: 4, shardCondition: 'negative-tile' }
  ),
  relic(
    'precisionDynamo',
    'Precision Dynamo',
    'Settling on the center tile without any board bumps adds +36 power.',
    'Epic',
    'precision-dynamo',
    'clean-center',
    36
  ),
];

const extraRelics = NEW_RELICS.map(
  ([id, name, description, rarity, animation, condition, bonus, extra]) =>
    relic(id, name, description, rarity, animation, condition, bonus, extra)
);

const BOSS_ONLY_RELICS = [
  relic(
    'chaosCapacitor',
    'Chaos Capacitor',
    'Each clash adds a random 1–8 power to your Core attack.',
    'Common',
    'chaos-capacitor',
    null,
    0,
    { bossOnly: true, bossRandomDamage: 8, shopPrice: 3 }
  ),
  relic(
    'rouletteAegis',
    'Roulette Aegis',
    'Each clash randomly blocks 0–3 damage from the Core.',
    'Uncommon',
    'roulette-aegis',
    null,
    0,
    { bossOnly: true, bossRandomBlock: 3, shopPrice: 5 }
  ),
  relic(
    'criticalMatrix',
    'Critical Matrix',
    'Each clash has a 1-in-5 chance to double your total Core attack.',
    'Rare',
    'critical-matrix',
    null,
    0,
    { bossOnly: true, bossDoubleChance: 0.2, shopPrice: 7 }
  ),
  relic(
    'naniteSwarm',
    'Nanite Swarm',
    'Each clash grants + 0–3 Max HP before the Core retaliates.',
    'Epic',
    'nanite-swarm',
    null,
    0,
    { bossOnly: true, bossRandomHeal: 3, shopPrice: 9 }
  ),
];

const ACTIVE_RELICS = [
  relic(
    'bountyTag',
    'Bounty Tag',
    'Before a guardian throw, mark one tile. Land there and defeat the guardian to earn 2 extra shards. The mark is spent on that throw, even if you miss; it can be used again on later throws. Not available during the Core fight.',
    'Rare',
    'bounty-tag',
    null,
    0,
    { activeAbility: 'bountyTag', bossUsable: false }
  ),
  relic(
    'chainReaction',
    'Chain Reaction',
    'Arm before a throw to trigger the landing tile, then its up, right, down, and left neighbors once each. Power Up landings keep it armed until the die lands on a different tile type. Neighboring Power Ups add their bonus without relaunching. Good and bad effects apply. One activation for the entire run. Recharge cannot restore it.',
    'Epic',
    'chain-reaction',
    null,
    0,
    { activeAbility: 'chainReaction', bossUsable: true }
  ),
  relic(
    'lastStandProtocol',
    'Last Stand Protocol',
    'When a hit would be lethal, make one survival throw. Beat 75 after board and roll-relic bonuses to survive at 1 HP. Consumed after the attempt, whether it succeeds or fails.',
    'Legendary',
    'last-stand-protocol',
    null,
    0,
    { activeAbility: 'lastStandProtocol', bossUsable: true, consumable: true }
  ),
  relic(
    'decoyBeacon',
    'Decoy Beacon',
    'Set up a decoy before starting the Core’s turn. It takes one hit for you. If the attack misses or is fully blocked, you keep the decoy for later. One use per Core fight.',
    'Epic',
    'decoy-beacon',
    null,
    0,
    { activeAbility: 'decoyBeacon', bossUsable: true }
  ),
  relic(
    'ghostBump',
    'Ghost Bump',
    'Once per Core fight, nudge the Core’s moving die. Tap toward a tile or press Space. This may change its number or landing tile.',
    'Epic',
    'ghost-bump',
    null,
    0,
    { activeAbility: 'ghostBump', bossUsable: true }
  ),
  relic(
    'phaseAnchor',
    'Phase Anchor',
    'Tap during your throw to slow your die for 4 seconds, giving you more time to aim a bump. One use per area.',
    'Rare',
    'phase-anchor',
    null,
    0,
    { activeAbility: 'phaseAnchor', bossUsable: true }
  ),
];

// Keep this retired ID readable while upgrading older saved runs.
const LEGACY_RELICS = [
  relic(
    'portalPair',
    'Portal Pair',
    'This relic has been retired. Paired portals now appear as random board obstacles.',
    'Epic',
    'portal-pair',
    null,
    0,
    { category: 'Special', deprecated: true }
  ),
];

export const RELICS = Object.fromEntries(
  [
    ...ORIGINAL_RELICS,
    ...extraRelics,
    ...BOARD_RELICS,
    ...TACTICAL_RELICS,
    ...BOSS_ONLY_RELICS,
    ...ACTIVE_RELICS,
    ...LEGACY_RELICS,
  ].map((entry) => [entry.id, entry])
);

const RELIC_RARITIES = ['Common', 'Uncommon', 'Rare', 'Epic', 'Legendary'];
const RELIC_RARITY_WEIGHTS = {
  Common: 50,
  Uncommon: 25,
  Rare: 18,
  Epic: 5,
  Legendary: 2,
};

export const RELIC_CATEGORY_WEIGHTS = {
  ...RELIC_RARITY_WEIGHTS,
};
export const RELIC_CHEST_WEIGHTS = {
  Special: 30,
  Rare: 40,
  Epic: 20,
  Legendary: 10,
};

const relicsByRarity = (rarity, owned = [], includeBossOnly = false) =>
  Object.keys(RELICS).filter(
    (id) =>
      RELICS[id].rarity === rarity &&
      !RELICS[id].deprecated &&
      !owned.includes(id) &&
      (includeBossOnly || !RELICS[id].bossOnly)
  );

const BOSS_RELIC_IDS = new Set([
  'mirrorChip',
  'firewall',
  'patchKit',
  'scavenger',
  'vaultKey',
  'lastStand',
]);

const BOSS_ROLL_CONDITIONS = new Set([
  'always',
  'odd',
  'even',
  'low',
  'low20',
  'low25',
  'high',
  'high40',
  'very-high',
  'contains7',
  'double',
  'different',
  'multiple5',
  'multiple10',
  'low35',
  'low50',
  'multiple3',
  'heal-tile',
  'shield-tile',
  'critical-tile',
  'negative-tile',
  'three-tiles',
  'anchored',
  'bumped',
  'no-bumps',
  'linked-throw',
  'powered',
  'full-power',
  'first-attack',
  'follow-up',
  'wounded-enemy',
  'low-roll-low-hp',
  'ascending-digits',
  'ends-one',
  'low-hp',
  'positive-tile',
  'shard-tile',
  'center-row',
  'mirrored',
  'soft-launch',
  'two-bumps',
  'descending-digits',
  'nonpositive',
  'force-field',
  'ice-landing',
  'low-gravity-bumps',
  'corner-tile',
  'measured-launch',
  'relay-routed',
  'clean-center',
]);

export const isBossRelic = (id, game = null) => {
  const definition = RELICS[id];
  const context = game && typeof game === 'object' ? game : null;
  if (definition?.boardConditionTypes && context) {
    const condition = ['shop', 'relic'].includes(context.phase)
      ? getRoomBoardCondition({ ...context, floor: CORE_SECTOR }, 'boss')
      : context.boardCondition ||
        getRoomBoardCondition({ ...context, floor: CORE_SECTOR }, 'boss');
    if (!definition.boardConditionTypes.includes(condition?.type)) return false;
  }
  return (
    !!definition &&
    (definition.bossOnly ||
      (definition.activeAbility && definition.bossUsable) ||
      BOSS_RELIC_IDS.has(id) ||
      definition.healAmount > 0 ||
      definition.damageReduction > 0 ||
      (definition.rollBonus > 0 &&
        BOSS_ROLL_CONDITIONS.has(definition.rollCondition)))
  );
};

export const getRelicCounts = () =>
  Object.fromEntries(
    RELIC_RARITIES.map((rarity) => [rarity, relicsByRarity(rarity).length])
  );
